import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CommonService from '../../../services/common.service';
import { toast } from 'react-toastify';
import { useCurrencyMaster } from '../../../hooks/useCurrencyMaster';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import StorageService from '../../../services/StorageService';
import '../../../assets/styles/Seller_quotation_accept.scss';

export default function SellerQuotationAccept() {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState({
    supplierInfo: true,
    itemsRequested: true,
    pricingTerms: true,
    bulkPricing: true,
    attachments: true,
    termsConditions: true
  });
  const [showStartOrderModal, setShowStartOrderModal] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');
  const [notifySupplier, setNotifySupplier] = useState(true);
  const [orderFile, setOrderFile] = useState(null);
  const orderFileInputRef = useRef(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [detailedRemarks, setDetailedRemarks] = useState('');
  const [notifySupplierReject, setNotifySupplierReject] = useState(false);
  const [rejectFile, setRejectFile] = useState(null);
  const rejectFileInputRef = useRef(null);
  const [quotationData, setQuotationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rfqData, setRfqData] = useState(null);
  const [quotationId, setQuotationId] = useState(null); // Store quotation_id for API calls
  const [processing, setProcessing] = useState(false); // Track accept/reject processing
  const [showQualityInspectorModal, setShowQualityInspectorModal] = useState(false);

  // Get current user information
  const currentUser = StorageService.getUser();
  const buyerName = currentUser?.name || 'User';

  // Master Data Hooks
  const { currencyOptions } = useCurrencyMaster();
  const { attributeUnitOptions, loading: uomLoading } = useProductDetailsMaster();

  // Helper function to get currency label from value (ID or code)
  const getCurrencyLabel = (currencyValue) => {
    if (!currencyValue) return 'INR';
    if (!currencyOptions || currencyOptions.length === 0) return currencyValue;
    
    const foundByValue = currencyOptions.find(opt => 
      String(opt.value) === String(currencyValue) || 
      opt.currencyCode === String(currencyValue)
    );
    if (foundByValue) {
      return foundByValue.currencyCode || foundByValue.label || currencyValue;
    }
    
    const foundByCode = currencyOptions.find(opt => 
      opt.currencyCode === String(currencyValue).toUpperCase()
    );
    if (foundByCode) {
      return foundByCode.currencyCode || foundByCode.label || currencyValue;
    }
    
    return currencyValue;
  };

  // Helper function to get currency symbol from currency code
  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return '';
    
    // Get symbol from currencyOptions (API data)
    if (currencyOptions && currencyOptions.length > 0) {
      const foundByCode = currencyOptions.find(opt => 
        opt.currencyCode === String(currencyCode).toUpperCase() ||
        String(opt.value) === String(currencyCode) ||
        opt.currencyCode === String(currencyCode)
      );
      if (foundByCode && foundByCode.currencySymbol) {
        return foundByCode.currencySymbol;
      }
    }
    
    // If no symbol found, return empty string (don't show code as fallback)
    return '';
  };

  // Helper function to get unit label from value (ID or label)
  const getUnitLabel = (unitValue) => {
    if (!unitValue) return 'Units';
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) return unitValue;
    
    // Convert both to numbers for comparison if possible
    const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
    
    // First try to find by value (ID) - handle both string and numeric comparison
    const foundByValue = attributeUnitOptions.find(opt => {
      // Try exact string match first
      if (String(opt.value) === String(unitValue)) return true;
      // Try numeric match if both are numbers
      if (unitValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === unitValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    
    // If not found by ID, try to find by label (might already be a label)
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue) || 
      opt.label?.toLowerCase() === String(unitValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return unitValue;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Get full file URL from attachment path
  const getFileUrl = (attachmentPath) => {
    if (!attachmentPath) return null;
    const baseUrl = 'http://localhost:3000';
    
    // If already a full URL, return as is
    if (attachmentPath.startsWith('http://') || attachmentPath.startsWith('https://')) {
      return attachmentPath;
    }
    
    // Ensure path starts with /
    const cleanPath = attachmentPath.startsWith('/') ? attachmentPath : '/' + attachmentPath;
    return `${baseUrl}${cleanPath}`;
  };

  // Transform API data to match UI structure
  const transformQuotationData = (quotation, rfq) => {
    const quotationId = quotation.quotation_number || `QUO-${quotation.quotation_id}`;
    const rfqNumber = quotation.rfq_id ? `REQ-${quotation.rfq_id}` : 'N/A';
    
    // Format dates
    const quoteDate = quotation.quote_date 
      ? new Date(quotation.quote_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      : quotation.created_at 
        ? new Date(quotation.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : 'N/A';
    
    let validUntil = 'N/A';
    if (quotation.quote_date && quotation.validity_days) {
      const quoteDateObj = new Date(quotation.quote_date);
      quoteDateObj.setDate(quoteDateObj.getDate() + parseInt(quotation.validity_days));
      validUntil = quoteDateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    }

    // Get status
    const status = quotation.quote_status || 'Pending Review';

    // Get supplier information
    // Debug: Log what we're receiving
    console.log('🔍 Supplier Info Debug (transformQuotationData):', {
      seller_email: quotation.seller_email,
      seller_phone: quotation.seller_phone,
      seller_address: quotation.seller_address,
      seller_contact_details: quotation.seller_contact_details,
      seller_company_name: quotation.seller_company_name,
      seller_name: quotation.seller_name,
      quotation_id: quotation.quotation_id,
      seller_user_id: quotation.seller_user_id,
      hasEmail: !!quotation.seller_email,
      hasPhone: !!quotation.seller_phone,
      hasAddress: !!quotation.seller_address,
      hasContactDetails: !!quotation.seller_contact_details
    });
    
    // Parse seller address from company_profile (JSON field)
    let supplierAddress = 'N/A';
    if (quotation.seller_address) {
      try {
        const addressData = typeof quotation.seller_address === 'string' 
          ? JSON.parse(quotation.seller_address) 
          : quotation.seller_address;
        
        if (addressData && typeof addressData === 'object' && !Array.isArray(addressData)) {
          const addressParts = [];
          if (addressData.address_line1) addressParts.push(addressData.address_line1);
          if (addressData.address_line2) addressParts.push(addressData.address_line2);
          if (addressData.city) addressParts.push(addressData.city);
          if (addressData.state) addressParts.push(addressData.state);
          if (addressData.country) addressParts.push(addressData.country);
          if (addressData.pincode) addressParts.push(addressData.pincode);
          supplierAddress = addressParts.length > 0 ? addressParts.join(', ') : 'N/A';
        } else if (typeof addressData === 'string') {
          // If it's already a string, use it directly
          supplierAddress = addressData;
        }
      } catch (e) {
        console.warn('Error parsing seller address:', e, quotation.seller_address);
        // Fallback: try to use shipping address if seller address parsing fails
        if (quotation.shipping_address) {
          const addressParts = [];
          if (quotation.shipping_address) addressParts.push(quotation.shipping_address);
          if (quotation.shipping_city) addressParts.push(quotation.shipping_city);
          if (quotation.shipping_state) addressParts.push(quotation.shipping_state);
          if (quotation.shipping_country) addressParts.push(quotation.shipping_country);
          if (quotation.shipping_pincode) addressParts.push(quotation.shipping_pincode);
          supplierAddress = addressParts.length > 0 ? addressParts.join(', ') : 'N/A';
        }
      }
    } else if (quotation.shipping_address) {
      // Fallback to shipping address if seller address is not available
      const addressParts = [];
      if (quotation.shipping_address) addressParts.push(quotation.shipping_address);
      if (quotation.shipping_city) addressParts.push(quotation.shipping_city);
      if (quotation.shipping_state) addressParts.push(quotation.shipping_state);
      if (quotation.shipping_country) addressParts.push(quotation.shipping_country);
      if (quotation.shipping_pincode) addressParts.push(quotation.shipping_pincode);
      supplierAddress = addressParts.length > 0 ? addressParts.join(', ') : 'N/A';
    }
    
    // Parse contact details from company_profile (JSON field)
    let supplierEmail = quotation.seller_email || 'N/A';
    let supplierPhone = quotation.seller_phone || 'N/A';
    
    if (quotation.seller_contact_details) {
      try {
        const contactData = typeof quotation.seller_contact_details === 'string' 
          ? JSON.parse(quotation.seller_contact_details) 
          : quotation.seller_contact_details;
        
        if (contactData && typeof contactData === 'object' && !Array.isArray(contactData)) {
          // Use contact_details email/phone if available, otherwise fallback to user_login
          if (contactData.contact_email) supplierEmail = contactData.contact_email;
          else if (contactData.alternate_email) supplierEmail = contactData.alternate_email;
          
          if (contactData.contact_number) supplierPhone = contactData.contact_number;
          else if (contactData.alternate_contact_number) supplierPhone = contactData.alternate_contact_number;
        }
      } catch (e) {
        console.warn('Error parsing seller contact details:', e, quotation.seller_contact_details);
      }
    }
    
    const supplier = {
      companyName: quotation.seller_company_name || quotation.seller_name || 'Unknown Supplier',
      email: supplierEmail,
      address: supplierAddress,
      contactPerson: quotation.seller_name || 'N/A',
      phone: supplierPhone
    };

    // Get line items
    const lineItems = quotation.items || [];
    
    // Get trade type and export tax type
    const tradeType = quotation.trade_type || 'IN India to India - Inter-State (Different States)';
    const exportTaxType = quotation.export_tax_type || null;
    
    // Calculate totals
    const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0);
    const cgstTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.cgst_amount) || 0), 0);
    const sgstTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.sgst_amount) || 0), 0);
    const igstTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.igst_amount) || 0), 0);
    const cessTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.cess_amount) || 0), 0);
    const customsTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.customs_amount) || 0), 0);
    const bcdTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.bcd_amount) || 0), 0);
    const swsTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.sws_amount) || 0), 0);
    const taxTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.tax_amount) || 0), 0);
    const shippingCost = parseFloat(quotation.freight_charges || 0) + 
                        parseFloat(quotation.insurance_charges || 0) + 
                        parseFloat(quotation.handling_clearance || 0);
    const grandTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);

    // Get currency
    const currencyCode = getCurrencyLabel(quotation.currency || 'INR');
    const currencySymbol = getCurrencySymbol(currencyCode);

    // Transform line items for pricing table
    const pricingItems = lineItems.map((item, index) => {
      return {
        item: item.description || `Item ${index + 1}`,
        description: '',
        qty: parseFloat(item.quantity) || 0,
        unit: getUnitLabel(item.uom) || 'Units',
        unitPrice: parseFloat(item.unit_price) || 0,
        discount: parseFloat(item.discount) || 0,
        subtotal: parseFloat(item.subtotal) || 0,
        // Tax fields
        cgst: parseFloat(item.cgst) || 0,
        sgst: parseFloat(item.sgst) || 0,
        igst: parseFloat(item.igst) || 0,
        cess: parseFloat(item.cess) || 0,
        customs: parseFloat(item.customs) || 0,
        bcdAmount: parseFloat(item.bcd_amount) || 0,
        swsAmount: parseFloat(item.sws_amount) || 0,
        tax: parseFloat(item.tax) || 0,
        taxRate: parseFloat(item.tax) || parseFloat(item.igst) || parseFloat(item.cgst) || 0,
        total: parseFloat(item.total) || 0
      };
    });

    // Transform line items for items requested
    const itemsRequested = lineItems.map((item, index) => {
      let certifications = [];
      if (item.certifications) {
        if (Array.isArray(item.certifications)) {
          certifications = item.certifications;
        } else if (typeof item.certifications === 'string') {
          try {
            certifications = JSON.parse(item.certifications || '[]');
          } catch (e) {
            console.warn('Error parsing certifications:', e);
          }
        }
      }

      return {
        item: item.description || `Item ${index + 1}`,
        description: '',
        specifications: item.description || 'N/A',
        qty: parseFloat(item.quantity) || 0,
        unit: getUnitLabel(item.uom) || 'Units',
        hsnSacCode: item.hsn_code || 'N/A',
        countryOfOrigin: item.country_of_origin || 'N/A',
        warrantyPeriod: item.warranty_period || 'N/A',
        certifications: certifications,
        sampleDetails: {
          offered: item.sample_offered || 'No',
          type: item.sample_type || 'N/A',
          quantity: item.sample_quantity ? `${item.sample_quantity} ${getUnitLabel(item.sample_unit) || ''}`.trim() : 'N/A',
          cost: item.sample_cost ? `${currencySymbol} ${parseFloat(item.sample_cost).toFixed(2)}` : 'N/A',
          dispatchLeadTime: item.dispatch_lead_time ? `${item.dispatch_lead_time} day(s)` : 'N/A',
          returnPolicy: item.return_policy || 'N/A'
        }
      };
    });

    // Get RFQ information
    const rfqNumberDisplay = rfq ? `REQ-${rfq.rfq_id}` : quotation.rfq_id ? `REQ-${quotation.rfq_id}` : 'N/A';
    const requestedDate = rfq?.created_at 
      ? new Date(rfq.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      : 'N/A';
    
    // Calculate required delivery date
    // Priority: 1. RFQ created_at + lead_time_days, 2. Quotation lead_time_days from quote_date, 3. Quotation delivery_time, 4. N/A
    let requiredBy = 'N/A';
    if (rfq?.created_at && rfq?.lead_time_days) {
      // Calculate from RFQ creation date + lead time days
      const reqDate = new Date(rfq.created_at);
      reqDate.setDate(reqDate.getDate() + parseInt(rfq.lead_time_days));
      requiredBy = reqDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } else if (quotation.quote_date && quotation.lead_time_days) {
      // Calculate from quotation date + lead time days
      const reqDate = new Date(quotation.quote_date);
      reqDate.setDate(reqDate.getDate() + parseInt(quotation.lead_time_days));
      requiredBy = reqDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } else if (quotation.delivery_time) {
      // Use delivery_time if it's a date string
      try {
        const deliveryDate = new Date(quotation.delivery_time);
        if (!isNaN(deliveryDate.getTime())) {
          requiredBy = deliveryDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        } else {
          // If delivery_time is not a valid date, use it as-is (might be a string like "45 days")
          requiredBy = quotation.delivery_time;
        }
      } catch (e) {
        // If parsing fails, use delivery_time as-is
        requiredBy = quotation.delivery_time;
      }
    }
    
    const specialRequirements = rfq?.special_requirements || 'N/A';

    // Parse payment terms
    let paymentTerms = {};
    if (quotation.payment_terms) {
      let paymentTermsParsed = [];
      try {
        paymentTermsParsed = typeof quotation.payment_terms === 'string' 
          ? JSON.parse(quotation.payment_terms) 
          : quotation.payment_terms;
      } catch (e) {
        console.warn('Error parsing payment_terms:', e);
      }
      
      const advancePayment = quotation.advance_payment || (paymentTermsParsed.find(pt => pt.label === 'Advance Payment')?.value) || 'N/A';
      const balancePayment = quotation.balance_payment || (paymentTermsParsed.find(pt => pt.label === 'Balance Payment')?.value) || 'N/A';
      const paymentDueDays = quotation.payment_due_days ? `${quotation.payment_due_days} days` : 'N/A';
      
      // Get currency option - try to match by ID first, then by currency code
      const currencyOption = currencyOptions?.find(opt => 
        String(opt.value) === String(quotation.currency) ||
        opt.currencyCode === String(quotation.currency) ||
        opt.currencyCode === String(quotation.currency).toUpperCase() ||
        String(opt.value) === String(currencyCode) ||
        opt.currencyCode === currencyCode ||
        opt.currencyCode === String(currencyCode).toUpperCase()
      );
      
      // Use the label from currencyOptions, not the raw currencyCode
      const currencyLabel = currencyOption?.label || currencyOption?.currencyCode || currencyCode;
      // Get symbol from API data only (no hardcoded fallback)
      const finalCurrencySymbol = currencyOption?.currencySymbol || currencySymbol || '';
      
      paymentTerms = {
        currency: finalCurrencySymbol || currencySymbol || '',
        paymentDueWithin: paymentDueDays,
        advancePayment: advancePayment,
        balancePayment: balancePayment
      };
    } else {
      // Get currency option - try to match by ID first, then by currency code
      const currencyOption = currencyOptions?.find(opt => 
        String(opt.value) === String(quotation.currency) ||
        opt.currencyCode === String(quotation.currency) ||
        opt.currencyCode === String(quotation.currency).toUpperCase() ||
        String(opt.value) === String(currencyCode) ||
        opt.currencyCode === currencyCode ||
        opt.currencyCode === String(currencyCode).toUpperCase()
      );
      
      // Use the label from currencyOptions, not the raw currencyCode
      const currencyLabel = currencyOption?.label || currencyOption?.currencyCode || currencyCode;
      // Get symbol from API data only (no hardcoded fallback)
      const finalCurrencySymbol = currencyOption?.currencySymbol || currencySymbol || '';
      
      paymentTerms = {
        currency: finalCurrencySymbol || currencySymbol || '',
        paymentDueWithin: 'N/A',
        advancePayment: 'N/A',
        balancePayment: 'N/A'
      };
    }

    // Get delivery terms
    const deliveryLeadTime = quotation.lead_time_days 
      ? `${quotation.lead_time_days} day(s) after initial payment`
      : quotation.delivery_time || 'N/A';
    const incoterms = quotation.trade_terms || quotation.incoterms || 'N/A';

    // Get warranty
    const warranty = quotation.warranty || 'N/A';

    // Transform bulk pricing from line items
    const bulkPricing = [];
    lineItems.forEach((item, itemIndex) => {
      if (item.bulk_pricing && Array.isArray(item.bulk_pricing) && item.bulk_pricing.length > 0) {
        item.bulk_pricing.forEach((tier, tierIndex) => {
          const minQty = tier.minQty || tier.min_qty || 0;
          const maxQty = tier.maxQty || tier.max_qty || null;
          const unitPrice = parseFloat(tier.unitPrice || tier.unit_price || 0);
          const discount = parseFloat(tier.discount || 0);
          
          // Calculate savings (difference from base price)
          const basePrice = parseFloat(item.unit_price) || 0;
          const savings = basePrice > 0 && discount > 0 ? basePrice - unitPrice : null;
          
          bulkPricing.push({
            itemNumber: itemIndex + 1,
            itemDescription: item.description || `Item ${itemIndex + 1}`,
            tierNumber: tierIndex + 1,
            quantityRange: maxQty ? `${minQty}-${maxQty} units` : `${minQty}+ units`,
            unitPrice: unitPrice,
            discount: discount,
            savings: savings
          });
        });
      }
    });

    // Transform attachments
    const attachments = [];
    if (quotation.attachments && Array.isArray(quotation.attachments) && quotation.attachments.length > 0) {
      quotation.attachments.forEach((attachment) => {
        if (typeof attachment === 'string') {
          // If attachment is just a filename string
          const fileName = attachment.split('/').pop() || attachment;
          const fileExtension = fileName.split('.').pop()?.toUpperCase() || 'FILE';
          attachments.push({
            name: fileName,
            type: fileExtension,
            size: 'N/A',
            uploadDate: quoteDate,
            url: attachment
          });
        } else if (typeof attachment === 'object' && attachment.name) {
          // If attachment is an object with name, size, etc.
          attachments.push({
            name: attachment.name,
            type: attachment.type || attachment.name.split('.').pop()?.toUpperCase() || 'FILE',
            size: attachment.size || 'N/A',
            uploadDate: attachment.uploadDate || quoteDate,
            url: attachment.url || attachment.path || attachment.name
          });
        }
      });
    }

    return {
      quotationNumber: quotationId,
      rfqNumber: rfqNumberDisplay,
      quoteDate: quoteDate,
      validUntil: validUntil,
      status: status,
      requiredDeliveryDate: requiredBy,
      grandTotal: grandTotal,
      supplier: supplier,
      itemsRequested: {
        rfqNumber: rfqNumberDisplay,
        requestedDate: requestedDate,
        requiredBy: requiredBy,
        items: itemsRequested,
        specialRequirements: specialRequirements
      },
      pricing: {
        items: pricingItems,
        subtotal: subtotal,
        cgstTotal: cgstTotal,
        sgstTotal: sgstTotal,
        igstTotal: igstTotal,
        cessTotal: cessTotal,
        customsTotal: customsTotal,
        bcdTotal: bcdTotal,
        swsTotal: swsTotal,
        tax: taxTotal,
        shippingCost: shippingCost,
        grandTotal: grandTotal
      },
      tradeType: tradeType,
      exportTaxType: exportTaxType,
      terms: {
        payment: paymentTerms,
        warranty: {
          period: warranty,
          coverage: 'As per quotation terms'
        },
        delivery: {
          leadTime: deliveryLeadTime,
          incoterms: incoterms
        },
        additionalNotes: quotation.supplier_description || null
      },
      bulkPricing: bulkPricing.length > 0 ? bulkPricing : (pricingItems.length > 0 ? [{
        itemNumber: 1,
        itemDescription: pricingItems[0].item || 'Item 1',
        tierNumber: 1,
        quantityRange: '1+ units',
        unitPrice: pricingItems[0].unitPrice,
        discount: 0,
        savings: null
      }] : []),
      attachments: attachments,
      currency: currencyCode,
      currencySymbol: currencySymbol
    };
  };

  // Fetch quotation and RFQ data
  useEffect(() => {
    const fetchQuotationData = async () => {
      try {
        setLoading(true);
        const quotationFromState = location.state?.quotation;
        
        if (!quotationFromState || !quotationFromState.quotation_id) {
          toast.error('Quotation data not found');
          navigate('/seller-quotation-list');
          return;
        }

        const quotationId = quotationFromState.quotation_id;
        
        // Log what we have in cached state
        console.log('📦 Cached State Data (quotationFromState):', {
          hasSellerEmail: !!quotationFromState.seller_email,
          hasSellerPhone: !!quotationFromState.seller_phone,
          hasSellerAddress: !!quotationFromState.seller_address,
          hasSellerContactDetails: !!quotationFromState.seller_contact_details,
          seller_user_id: quotationFromState.seller_user_id,
          quotation_id: quotationFromState.quotation_id
        });
        
        // Fetch quotation details - try buyer endpoint first, fallback to seller endpoint
        let quotationResponse;
        try {
          // Try to get quotation from buyer's quotations list (already have it in state)
          // But fetch full details to ensure we have everything
          console.log('🔄 Fetching quotation from API...');
          quotationResponse = await CommonService.get(`/quotation/${quotationId}`);
          console.log('✅ API call succeeded');
        } catch (quoteError) {
          // If seller endpoint fails, use data from state
          console.error('❌ API call failed:', quoteError?.response?.status, quoteError?.response?.data);
          if (quoteError?.response?.status === 403 || quoteError?.response?.status === 401) {
            console.warn('⚠️ API call failed with 403/401, using cached data. Seller info may be incomplete.');
            // Use quotation data from state if API call fails due to permissions
            const transformed = transformQuotationData(quotationFromState, null);
            setQuotationData(transformed);
            setLoading(false);
            return;
          }
          throw quoteError;
        }
        
        if (!quotationResponse || !quotationResponse.success || !quotationResponse.data) {
          console.warn('⚠️ API response was not successful, using cached data. Seller info may be incomplete.');
          console.log('API Response:', quotationResponse);
          // Fallback to state data
          const transformed = transformQuotationData(quotationFromState, null);
          setQuotationData(transformed);
          setLoading(false);
          return;
        }

        const quotation = quotationResponse.data;
        console.log('✅ API Response - Seller Info:', {
          seller_email: quotation.seller_email,
          seller_phone: quotation.seller_phone,
          seller_address: quotation.seller_address,
          seller_contact_details: quotation.seller_contact_details,
          seller_company_name: quotation.seller_company_name,
          seller_name: quotation.seller_name,
          seller_user_id: quotation.seller_user_id,
          quotation_id: quotation.quotation_id
        });
        
        // Store quotation_id for API calls
        setQuotationId(quotation.quotation_id);
        const rfqId = quotation.rfq_id;

        // Fetch RFQ details
        let rfq = null;
        if (rfqId) {
          try {
            const rfqResponse = await CommonService.get(`/rfq/${rfqId}`);
            if (rfqResponse && rfqResponse.success && rfqResponse.data) {
              rfq = rfqResponse.data;
            }
          } catch (rfqError) {
            console.warn('Error fetching RFQ details:', rfqError);
            // Continue without RFQ data if fetch fails
          }
        }

        // Transform quotation data to match UI structure
        const transformedData = transformQuotationData(quotation, rfq);
        setQuotationData(transformedData);
        setRfqData(rfq);
      } catch (error) {
        console.error('Error fetching quotation data:', error);
        
        // Handle 401 Unauthorized - redirect to login
        if (error?.response?.status === 401) {
          toast.error('Your session has expired. Please login again.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 1500);
          return;
        }
        
        // Try to use data from state as fallback
        const quotationFromState = location.state?.quotation;
        if (quotationFromState) {
          // Store quotation_id for API calls
          if (quotationFromState.quotation_id) {
            setQuotationId(quotationFromState.quotation_id);
          }
          
          // Try to fetch RFQ data even when using cached quotation
          let rfq = null;
          if (quotationFromState.rfq_id) {
            try {
              const rfqResponse = await CommonService.get(`/rfq/${quotationFromState.rfq_id}`);
              if (rfqResponse && rfqResponse.success && rfqResponse.data) {
                rfq = rfqResponse.data;
              }
            } catch (rfqError) {
              console.warn('Error fetching RFQ details for cached quotation:', rfqError);
              // Continue without RFQ data
            }
          }
          
          const transformed = transformQuotationData(quotationFromState, rfq);
          setQuotationData(transformed);
          setRfqData(rfq);
          setLoading(false);
          // Only show warning if RFQ data couldn't be fetched
          if (!rfq && quotationFromState.rfq_id) {
            toast.warn('Some RFQ details may be incomplete.');
          }
        } else {
          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch quotation details';
          toast.error(errorMessage);
          navigate('/seller-quotation-list');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchQuotationData();
  }, [location.state, navigate]);

  // Re-transform quotation data when master data (units) becomes available
  useEffect(() => {
    // Only re-transform if master data is loaded and we have quotation data
    if (!uomLoading && attributeUnitOptions && attributeUnitOptions.length > 0 && quotationData && rfqData) {
      // Check if units need updating (if they're still IDs instead of labels)
      const needsUpdate = quotationData.pricing?.items?.some(item => {
        // Check if unit is a numeric ID (not a label)
        const unitValue = item.unit;
        if (!unitValue || unitValue === 'Units') return false;
        // Check if it's a numeric value that should be converted to label
        const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
        if (unitValueNum !== null) {
          // Check if this numeric value is actually a unit ID (not a valid unit label)
          const isUnitId = attributeUnitOptions.some(opt => Number(opt.value) === unitValueNum);
          return isUnitId; // Only update if it's actually a unit ID
        }
        return false;
      }) || quotationData.itemsRequested?.items?.some(item => {
        const unitValue = item.unit;
        if (!unitValue || unitValue === 'Units') return false;
        const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
        if (unitValueNum !== null) {
          const isUnitId = attributeUnitOptions.some(opt => Number(opt.value) === unitValueNum);
          return isUnitId;
        }
        return false;
      });

      if (needsUpdate) {
        console.log('🔄 Re-transforming quotation data with unit master data now available');
        // Re-transform with updated master data
        const quotationFromState = location.state?.quotation;
        if (quotationFromState && quotationFromState.quotation_id) {
          const transformed = transformQuotationData(quotationFromState, rfqData);
          setQuotationData(transformed);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uomLoading, attributeUnitOptions?.length, quotationData?.pricing?.items?.length, quotationData?.itemsRequested?.items?.length]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatCurrency = (amount, currencyCode = 'INR', currencySymbol = '') => {
    if (!amount && amount !== 0) return '-';
    const symbol = currencySymbol || getCurrencySymbol(currencyCode);
    const formattedAmount = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return symbol ? `${symbol} ${formattedAmount}` : formattedAmount;
  };

  const formatCurrencyWithDecimals = (amount, currencyCode = 'INR', currencySymbol = '') => {
    if (!amount && amount !== 0) return '-';
    const symbol = currencySymbol || getCurrencySymbol(currencyCode);
    const formattedAmount = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return symbol ? `${symbol} ${formattedAmount}` : formattedAmount;
  };

  const formatDateTime = () => {
    const now = new Date();
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(now);
  };

  const handleOrderFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setOrderFile(file);
    }
  };

  const handleConfirmOrder = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!quotationId) {
      toast.error('Quotation ID not found');
      return;
    }

    try {
      setProcessing(true);
      
      // Upload order file if provided
      let acceptanceAttachmentPath = null;
      if (orderFile) {
        try {
          const formData = new FormData();
          formData.append('file', orderFile);
          formData.append('category', 'quotation');
          formData.append('companyName', 'Quotation-Acceptance');
          formData.append('fileType', 'acceptance_attachment');

          const uploadResponse = await CommonService.post('/upload/single', formData);
          if (uploadResponse && uploadResponse.success && uploadResponse.data) {
            acceptanceAttachmentPath = uploadResponse.data.accessPath || uploadResponse.data.filePath || null;
          }
        } catch (uploadError) {
          console.error('Error uploading acceptance file:', uploadError);
          toast.warning('Quotation accepted, but file upload failed');
        }
      }
      
      // Prepare acceptance data
      const acceptanceData = {
        acceptanceNotes: orderNotes || null,
        acceptanceAttachment: acceptanceAttachmentPath,
        notifySupplier: notifySupplier || false
      };
      
      // Call the accept quotation API endpoint
      const response = await CommonService.post(`/quotation/${quotationId}/accept`, acceptanceData);
      
      if (response && response.success) {
        const procurementApproval = response.data?.procurement_approval;
        const requiresProcurementApproval = procurementApproval?.required === true;

        if (requiresProcurementApproval) {
          toast.success('Order submitted for procurement approval. PO creation will be available after approval.');
          setQuotationData(prev => prev ? { ...prev, status: 'Pending Procurement Approval' } : null);
          setShowStartOrderModal(false);
          setOrderNotes('');
          setOrderFile(null);
          setNotifySupplier(true);
          const p2pTxId = procurementApproval?.p2p_transaction_id;
          if (p2pTxId) {
            navigate(`/dashboard/p2p/transactions/${p2pTxId}`, { replace: false });
          } else {
            navigate('/dashboard/p2p/my-approvals', { replace: false });
          }
          return;
        }

        toast.success('Quotation accepted successfully!');
        
        // Update local state to reflect the new status
        setQuotationData(prev => prev ? { ...prev, status: 'Accepted' } : null);
        
        // Close the modal and reset form immediately
        setShowStartOrderModal(false);
        setOrderNotes('');
        setOrderFile(null);
        setNotifySupplier(true);
        
        // Show Quality Inspector evaluation modal
        setShowQualityInspectorModal(true);
      } else {
        throw new Error(response?.message || 'Failed to accept quotation');
      }
    } catch (error) {
      console.error('Error accepting quotation:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to accept quotation';
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRejectFile(file);
    }
  };

  const handleOrderFileClick = () => {
    orderFileInputRef.current?.click();
  };

  const handleRejectFileClick = () => {
    rejectFileInputRef.current?.click();
  };

  const handleRejectQuotation = async () => {
    if (!quotationId) {
      toast.error('Quotation ID not found');
      return;
    }

    if (!rejectReason || !detailedRemarks) {
      toast.error('Please provide both rejection reason and detailed remarks');
      return;
    }

    try {
      setProcessing(true);
      
      // Upload rejection file if provided
      let rejectionAttachmentPath = null;
      if (rejectFile) {
        try {
          const formData = new FormData();
          formData.append('file', rejectFile);
          formData.append('category', 'quotation');
          formData.append('companyName', 'Quotation-Rejection');
          formData.append('fileType', 'rejection_attachment');

          const uploadResponse = await CommonService.post('/upload/single', formData);
          if (uploadResponse && uploadResponse.success && uploadResponse.data) {
            rejectionAttachmentPath = uploadResponse.data.accessPath || uploadResponse.data.filePath || null;
          }
        } catch (uploadError) {
          console.error('Error uploading rejection file:', uploadError);
          toast.warning('Quotation rejected, but file upload failed');
        }
      }

      // Call the reject quotation API endpoint
      const rejectData = {
        rejectionReason: rejectReason,
        rejectionRemarks: detailedRemarks,
        rejectionAttachment: rejectionAttachmentPath,
        notifySupplier: notifySupplierReject || false
      };

      const response = await CommonService.post(`/quotation/${quotationId}/reject`, rejectData);
      
      if (response && response.success) {
        toast.success('Quotation rejected successfully');
        
        // Update local state to reflect the new status
        setQuotationData(prev => prev ? { ...prev, status: 'Rejected' } : null);
        
        // Keep modal open briefly to show success, then close and navigate
        setTimeout(() => {
          // Close the modal and reset form
          setShowRejectModal(false);
          setRejectReason('');
          setDetailedRemarks('');
          setRejectFile(null);
          setNotifySupplierReject(false);
          
          // Navigate back to quotation list after showing success message
          setTimeout(() => {
            navigate('/seller-quotation-list', { 
              state: { activeTab: 'rejected' },
              replace: false 
            });
          }, 500);
        }, 1500);
      } else {
        throw new Error(response?.message || 'Failed to reject quotation');
      }
    } catch (error) {
      console.error('Error rejecting quotation:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to reject quotation';
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="seller-quotation-accept">
        <div className="sqa-main-content">
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>Loading quotation details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if no data
  if (!quotationData) {
    return (
      <div className="seller-quotation-accept">
        <div className="sqa-main-content">
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>Quotation data not available</p>
            <button onClick={() => navigate('/seller-quotation-list')} style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}>
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get status from location state or use default from quotationData
  const currentStatus = location.state?.status || quotationData.status;

  return (
    <div className="seller-quotation-accept">
      {/* Main Content */}
      <div className="sqa-main-content">
        {/* Navigation & Status Section */}
        <div className="sqa-nav-section">
          <div className="sqa-nav-left">
            <button className="sqa-back-link" onClick={() => navigate('/seller-quotation-list')}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              Back to List
            </button>
            <div className="sqa-title-section">
              <div className="sqa-title-row">
                <h1 className="sqa-main-title">Quotation Details</h1>
                <div className="sqa-status-badge">{currentStatus}</div>
              </div>
              <p className="sqa-subtitle">Review quotation details and take action</p>
            </div>
          </div>
          {currentStatus !== 'Accepted' && currentStatus !== 'Rejected' && (
            <div className="sqa-title-actions">
              <button className="sqa-title-action-btn sqa-title-btn-reject" onClick={() => setShowRejectModal(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                Reject
              </button>
              <button className="sqa-title-action-btn sqa-title-btn-sample" onClick={() => navigate('/sample-request', { state: { quotationId: quotationId || quotationData?.quotation_id } })}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
                Get Sample
              </button>
              <button className="sqa-title-action-btn sqa-title-btn-order" onClick={() => setShowStartOrderModal(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
                Start Order
              </button>
            </div>
          )}
        </div>

        {/* Quotation Information Card */}
        <div className="sqa-card">
          <h2 className="sqa-card-title-only">Quotation Information</h2>
          <div className="sqa-card-content">
            <div className="sqa-info-grid">
              <div className="sqa-info-item">
                <div className="sqa-info-label">Quotation Number</div>
                <div className="sqa-info-value">{quotationData.quotationNumber}</div>
              </div>
              <div className="sqa-info-item">
                <div className="sqa-info-label">RFQ Number</div>
                <div className="sqa-info-value">{quotationData.rfqNumber}</div>
              </div>
              <div className="sqa-info-item">
                <div className="sqa-info-label">Quote Date</div>
                <div className="sqa-info-value">{quotationData.quoteDate}</div>
              </div>
              <div className="sqa-info-item">
                <div className="sqa-info-label">Valid Until</div>
                <div className="sqa-info-value sqa-highlight-orange">{quotationData.validUntil}</div>
              </div>
              <div className="sqa-info-item sqa-status-item">
                <div className="sqa-info-label">Status</div>
                <div className="sqa-status-badge-inline">{currentStatus}</div>
              </div>
              <div className="sqa-info-item">
                <div className="sqa-info-label">Required Delivery Date</div>
                <div className="sqa-info-value">{quotationData.requiredDeliveryDate}</div>
              </div>
              <div className="sqa-info-item sqa-grand-total-item">
                <div className="sqa-info-label">Grand Total</div>
                <div className="sqa-info-value sqa-highlight-green">{formatCurrency(quotationData.grandTotal, quotationData.currency, quotationData.currencySymbol)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Supplier Information Card */}
        <div className="sqa-card">
          <div 
            className="sqa-card-header" 
            onClick={() => toggleSection('supplierInfo')}
          >
            <div className="sqa-card-header-left">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-card-icon">
                <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                <path d="M10 6h4"></path>
                <path d="M10 10h4"></path>
                <path d="M10 14h4"></path>
                <path d="M10 18h4"></path>
              </svg>
              <h2 className="sqa-card-title">Supplier Information</h2>
            </div>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              className="sqa-chevron"
              style={{ transform: expandedSections.supplierInfo ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s ease' }}
            >
              <path d="m18 15-6-6-6 6"></path>
            </svg>
          </div>
          {expandedSections.supplierInfo && (
            <div className="sqa-card-content">
              <div className="sqa-info-grid sqa-three-columns">
                <div className="sqa-info-item">
                  <div className="sqa-info-label">Company Name</div>
                  <div className="sqa-info-value">{quotationData.supplier.companyName}</div>
                </div>
                <div className="sqa-info-item">
                  <div className="sqa-info-label">Contact Person</div>
                  <div className="sqa-info-value">{quotationData.supplier.contactPerson}</div>
                </div>
                <div className="sqa-info-item">
                  <div className="sqa-info-label">Email</div>
                  <div className="sqa-info-value sqa-email-link">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                    </svg>
                    {quotationData.supplier.email}
                  </div>
                </div>
                <div className="sqa-info-item">
                  <div className="sqa-info-label">Phone</div>
                  <div className="sqa-info-value sqa-with-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    {quotationData.supplier.phone}
                  </div>
                </div>
                <div className="sqa-info-item sqa-address-item">
                  <div className="sqa-info-label">Address</div>
                  <div className="sqa-info-value sqa-with-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    {quotationData.supplier.address}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Items Requested Card */}
        <div className="sqa-card">
          <div 
            className="sqa-card-header" 
            onClick={() => toggleSection('itemsRequested')}
          >
            <div className="sqa-card-header-left">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-card-icon">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line x1="12" y1="22.08" x2="12" y2="12"></line>
              </svg>
              <h2 className="sqa-card-title">Items Requested</h2>
            </div>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              className="sqa-chevron"
              style={{ transform: expandedSections.itemsRequested ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s ease' }}
            >
              <path d="m18 15-6-6-6 6"></path>
            </svg>
          </div>
          {expandedSections.itemsRequested && (
            <div className="sqa-card-content">
              <div className="sqa-meta-info">
                <div className="sqa-meta-item">
                  <span className="sqa-meta-label">RFQ Number: </span>
                  <span className="sqa-meta-value">{quotationData.itemsRequested.rfqNumber}</span>
                </div>
                <div className="sqa-meta-item">
                  <span className="sqa-meta-label">Requested Date: </span>
                  <span className="sqa-meta-value">{quotationData.itemsRequested.requestedDate}</span>
                </div>
                <div className="sqa-meta-item">
                  <span className="sqa-meta-label">Required By: </span>
                  <span className="sqa-meta-value">{quotationData.itemsRequested.requiredBy}</span>
                </div>
              </div>
              
              {quotationData.itemsRequested.items.map((item, index) => (
                <div key={index} className="sqa-item-detail-card">
                  <div className="sqa-item-header">
                    <div className="sqa-item-header-left">
                      <div className="sqa-item-number">{index + 1}</div>
                      <div className="sqa-item-title">{item.item}</div>
                    </div>
                    <div className="sqa-item-header-right">
                      <div className="sqa-item-qty-label">Quantity</div>
                      <div className="sqa-item-qty">{item.qty} {item.unit}</div>
                    </div>
                  </div>
                  
                  
                  <div className="sqa-item-specifications">
                    <div className="sqa-item-section-label">Specifications</div>
                    <div className="sqa-item-section-value">{item.specifications}</div>
                  </div>
                  
                  <div className="sqa-item-divider"></div>
                  
                  <div className="sqa-item-compliance">
                    <div className="sqa-item-section-label sqa-compliance-header">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      </svg>
                      Item Details & Compliance Information
                    </div>
                    <div className="sqa-compliance-grid">
                      
                        <div className="sqa-compliance-item">
                          <span className="sqa-compliance-label">HSN / SAC Code</span>
                          <span className="sqa-compliance-value">{item.hsnSacCode}</span>
                        </div>
                        <div className="sqa-compliance-item">
                          <span className="sqa-compliance-label">Certifications / Standards Met</span>
                          <div className="sqa-certifications">
                            {item.certifications.map((cert, certIndex) => (
                              <span key={certIndex} className="sqa-cert-badge">
                                ✓ {cert}
                              </span>
                            ))}
                          </div>
                        </div>
                      
                        <div className="sqa-compliance-item">
                          <span className="sqa-compliance-label">Country of Origin</span>
                          <span className="sqa-compliance-value">{item.countryOfOrigin}</span>
                        </div>
                        <div className="sqa-compliance-item">
                          <span className="sqa-compliance-label">Warranty / Guarantee Period</span>
                          <span className="sqa-compliance-value">{item.warrantyPeriod}</span>
                        </div>
                      </div>
                    
                  </div>
                  
                  <div className="sqa-item-divider"></div>
                  
                  <div className="sqa-sample-section">
                    <div className="sqa-item-section-label">Sample Details for this Item</div>
                    <div className="sqa-sample-details">
                      <div className="sqa-sample-table">
                      <div className="sqa-sample-col">
                        <div className="sqa-sample-label">Sample Offered</div>
                        <div className="sqa-sample-value sqa-sample-yes">{item.sampleDetails.offered}</div>
                      </div>
                      <div className="sqa-sample-col">
                        <div className="sqa-sample-label">Sample Type</div>
                        <div className="sqa-sample-value">{item.sampleDetails.type}</div>
                      </div>
                      <div className="sqa-sample-col">
                        <div className="sqa-sample-label">Sample Quantity</div>
                        <div className="sqa-sample-value">{item.sampleDetails.quantity} {item.unit}</div>
                      </div>
                      <div className="sqa-sample-col">
                        <div className="sqa-sample-label">Sample Cost ({quotationData.currencySymbol || ''})</div>
                        <div className="sqa-sample-value">{item.sampleDetails.cost}</div>
                      </div>
                      <div className="sqa-sample-col">
                        <div className="sqa-sample-label">Dispatch Lead Time</div>
                        <div className="sqa-sample-value">{item.sampleDetails.dispatchLeadTime}</div>
                      </div>
                      <div className="sqa-sample-col">
                        <div className="sqa-sample-label">Return Policy</div>
                        <div className="sqa-sample-value">{item.sampleDetails.returnPolicy}</div>
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="sqa-special-requirements">
                <div className="sqa-special-requirements-content">
                  <div className="sqa-special-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-alert" aria-hidden="true">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" x2="12" y1="8" y2="12"></line>
                      <line x1="12" x2="12.01" y1="16" y2="16"></line>
                    </svg>
                  </div>
                  <div className="sqa-special-text">
                    <div className="sqa-special-title">Special Requirements</div>
                    <div className="sqa-special-description">{quotationData.itemsRequested.specialRequirements}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quotation Response - Pricing & Terms Card */}
        <div className="sqa-card">
          <div 
            className="sqa-card-header" 
            onClick={() => toggleSection('pricingTerms')}
          >
            <div className="sqa-card-header-left">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-card-icon">
                <line x1="12" x2="12" y1="2" y2="22"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              <h2 className="sqa-card-title">Quotation Response - Pricing & Terms</h2>
            </div>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              className="sqa-chevron"
              style={{ transform: expandedSections.pricingTerms ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s ease' }}
            >
              <path d="m18 15-6-6-6 6"></path>
            </svg>
          </div>
          {expandedSections.pricingTerms && (
            <div className="sqa-card-content">
              <div className="sqa-table-wrapper">
                <table className="sqa-table">
                  <thead>
                    <tr>
                      <th className="sqa-th-item">Item</th>
                      <th className="sqa-th-description">Description</th>
                      <th className="sqa-th-right">Qty</th>
                      <th className="sqa-th-unit">Unit</th>
                      <th className="sqa-th-right">Unit Price</th>
                      <th className="sqa-th-right">Disc %</th>
                      <th className="sqa-th-right">Subtotal</th>
                      {/* Conditional columns based on trade type */}
                      {quotationData.tradeType === 'IN India to India - Intra-State (Same State)' && (
                        <>
                          <th className="sqa-th-right">CGST %</th>
                          <th className="sqa-th-right">SGST %</th>
                          <th className="sqa-th-right">CESS %</th>
                        </>
                      )}
                      {quotationData.tradeType === 'IN India to India - Inter-State (Different States)' && (
                        <>
                          <th className="sqa-th-right">IGST %</th>
                          <th className="sqa-th-right">CESS %</th>
                        </>
                      )}
                      {quotationData.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && 
                        quotationData.exportTaxType !== 'LUT (Letter of Undertaking) - 0% IGST' && (
                        <>
                          <th className="sqa-th-right">IGST %</th>
                          <th className="sqa-th-right">CESS %</th>
                        </>
                      )}
                      {quotationData.tradeType === '🌎 →IN Import into India (Foreign → India)' && (
                        <>
                          <th className="sqa-th-right">Customs %</th>
                          <th className="sqa-th-right">IGST %</th>
                          <th className="sqa-th-right">CESS %</th>
                          <th className="sqa-th-right">BCD</th>
                          <th className="sqa-th-right">SWS</th>
                        </>
                      )}
                      {quotationData.tradeType === '🌎 Third Country Trade (Foreign → Foreign)' && (
                        <th className="sqa-th-right">Tax %</th>
                      )}
                      <th className="sqa-th-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotationData.pricing.items.map((item, index) => (
                      <tr key={index}>
                        <td className="sqa-td-item">{index + 1}</td>
                        <td className="sqa-td-description">{item.item}</td>
                        <td className="sqa-td-right">{item.qty}</td>
                        <td className="sqa-td-unit">{item.unit}</td>
                        <td className="sqa-td-right">{formatCurrency(item.unitPrice, quotationData.currency, quotationData.currencySymbol)}</td>
                        <td className="sqa-td-right">{item.discount}%</td>
                        <td className="sqa-td-right">{formatCurrency(item.subtotal, quotationData.currency, quotationData.currencySymbol)}</td>
                        {/* Conditional cells based on trade type */}
                        {quotationData.tradeType === 'IN India to India - Intra-State (Same State)' && (
                          <>
                            <td className="sqa-td-right">{item.cgst}%</td>
                            <td className="sqa-td-right">{item.sgst}%</td>
                            <td className="sqa-td-right">{item.cess}%</td>
                          </>
                        )}
                        {quotationData.tradeType === 'IN India to India - Inter-State (Different States)' && (
                          <>
                            <td className="sqa-td-right">{item.igst}%</td>
                            <td className="sqa-td-right">{item.cess}%</td>
                          </>
                        )}
                        {quotationData.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && 
                          quotationData.exportTaxType !== 'LUT (Letter of Undertaking) - 0% IGST' && (
                          <>
                            <td className="sqa-td-right">{item.igst}%</td>
                            <td className="sqa-td-right">{item.cess}%</td>
                          </>
                        )}
                        {quotationData.tradeType === '🌎 →IN Import into India (Foreign → India)' && (
                          <>
                            <td className="sqa-td-right">{item.customs}%</td>
                            <td className="sqa-td-right">{item.igst}%</td>
                            <td className="sqa-td-right">{item.cess}%</td>
                            <td className="sqa-td-right">{formatCurrency(item.bcdAmount, quotationData.currency, quotationData.currencySymbol)}</td>
                            <td className="sqa-td-right">{formatCurrency(item.swsAmount, quotationData.currency, quotationData.currencySymbol)}</td>
                          </>
                        )}
                        {quotationData.tradeType === '🌎 Third Country Trade (Foreign → Foreign)' && (
                          <td className="sqa-td-right">{item.tax}%</td>
                        )}
                        <td className="sqa-td-right">{formatCurrency(item.total, quotationData.currency, quotationData.currencySymbol)}</td>
                      </tr>
                    ))}
                    {/* Calculate colspan based on trade type */}
                    {(() => {
                      const baseCols = 7; // Item, Description, Qty, Unit, Unit Price, Disc %, Subtotal
                      let taxCols = 0;
                      if (quotationData.tradeType === 'IN India to India - Intra-State (Same State)') {
                        taxCols = 3; // CGST, SGST, CESS
                      } else if (quotationData.tradeType === 'IN India to India - Inter-State (Different States)') {
                        taxCols = 2; // IGST, CESS
                      } else if (quotationData.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && 
                                 quotationData.exportTaxType !== 'LUT (Letter of Undertaking) - 0% IGST') {
                        taxCols = 2; // IGST, CESS
                      } else if (quotationData.tradeType === '🌎 →IN Import into India (Foreign → India)') {
                        taxCols = 5; // Customs, IGST, CESS, BCD, SWS
                      } else if (quotationData.tradeType === '🌎 Third Country Trade (Foreign → Foreign)') {
                        taxCols = 1; // Tax
                      }
                      // Colspan should span from Qty to the last tax column (excluding Item, Description, and Total)
                      // That's: Qty, Unit, Unit Price, Disc %, Subtotal, and tax columns = 5 + taxCols
                      const subtotalColspan = 5 + taxCols; // Qty, Unit, Unit Price, Disc %, Subtotal + tax columns
                      
                      return (
                        <>
                    <tr className="sqa-summary-row">
                      <td></td>
                      <td className="sqa-summary-label">Subtotal</td>
                            <td colSpan={subtotalColspan}></td>
                            <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.subtotal, quotationData.currency, quotationData.currencySymbol)}</td>
                          </tr>
                          
                          {/* Intra-State: Show CGST, SGST, CESS */}
                          {quotationData.tradeType === 'IN India to India - Intra-State (Same State)' && (
                            <>
                              <tr className="sqa-summary-row">
                      <td></td>
                                <td className="sqa-summary-label">CGST</td>
                                <td colSpan={subtotalColspan}></td>
                                <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.cgstTotal, quotationData.currency, quotationData.currencySymbol)}</td>
                              </tr>
                              <tr className="sqa-summary-row">
                      <td></td>
                                <td className="sqa-summary-label">SGST</td>
                                <td colSpan={subtotalColspan}></td>
                                <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.sgstTotal, quotationData.currency, quotationData.currencySymbol)}</td>
                              </tr>
                              <tr className="sqa-summary-row">
                      <td></td>
                                <td className="sqa-summary-label">CESS</td>
                                <td colSpan={subtotalColspan}></td>
                                <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.cessTotal, quotationData.currency, quotationData.currencySymbol)}</td>
                              </tr>
                            </>
                          )}
                          
                          {/* Inter-State: Show IGST, CESS */}
                          {quotationData.tradeType === 'IN India to India - Inter-State (Different States)' && (
                            <>
                              <tr className="sqa-summary-row">
                      <td></td>
                                <td className="sqa-summary-label">IGST</td>
                                <td colSpan={subtotalColspan}></td>
                                <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.igstTotal, quotationData.currency, quotationData.currencySymbol)}</td>
                              </tr>
                              <tr className="sqa-summary-row">
                      <td></td>
                                <td className="sqa-summary-label">CESS</td>
                                <td colSpan={subtotalColspan}></td>
                                <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.cessTotal, quotationData.currency, quotationData.currencySymbol)}</td>
                    </tr>
                            </>
                          )}
                          
                          {/* Export: Show IGST, CESS (if not LUT) */}
                          {quotationData.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && 
                            quotationData.exportTaxType !== 'LUT (Letter of Undertaking) - 0% IGST' && (
                            <>
                    <tr className="sqa-summary-row">
                      <td></td>
                                <td className="sqa-summary-label">IGST</td>
                                <td colSpan={subtotalColspan}></td>
                                <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.igstTotal, quotationData.currency, quotationData.currencySymbol)}</td>
                              </tr>
                              <tr className="sqa-summary-row">
                      <td></td>
                                <td className="sqa-summary-label">CESS</td>
                                <td colSpan={subtotalColspan}></td>
                                <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.cessTotal, quotationData.currency, quotationData.currencySymbol)}</td>
                              </tr>
                            </>
                          )}
                          
                          {/* Import: Show Customs, IGST, CESS, BCD, SWS */}
                          {quotationData.tradeType === '🌎 →IN Import into India (Foreign → India)' && (
                            <>
                              <tr className="sqa-summary-row">
                      <td></td>
                                <td className="sqa-summary-label">Customs</td>
                                <td colSpan={subtotalColspan}></td>
                                <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.customsTotal, quotationData.currency, quotationData.currencySymbol)}</td>
                              </tr>
                              <tr className="sqa-summary-row">
                      <td></td>
                                <td className="sqa-summary-label">IGST</td>
                                <td colSpan={subtotalColspan}></td>
                                <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.igstTotal, quotationData.currency, quotationData.currencySymbol)}</td>
                              </tr>
                              <tr className="sqa-summary-row">
                      <td></td>
                                <td className="sqa-summary-label">CESS</td>
                                <td colSpan={subtotalColspan}></td>
                                <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.cessTotal, quotationData.currency, quotationData.currencySymbol)}</td>
                              </tr>
                              <tr className="sqa-summary-row">
                      <td></td>
                                <td className="sqa-summary-label">BCD</td>
                                <td colSpan={subtotalColspan}></td>
                                <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.bcdTotal, quotationData.currency, quotationData.currencySymbol)}</td>
                    </tr>
                    <tr className="sqa-summary-row">
                      <td></td>
                                <td className="sqa-summary-label">SWS</td>
                                <td colSpan={subtotalColspan}></td>
                                <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.swsTotal, quotationData.currency, quotationData.currencySymbol)}</td>
                              </tr>
                            </>
                          )}
                          
                          {/* Third Country Trade: Show Tax */}
                          {quotationData.tradeType === '🌎 Third Country Trade (Foreign → Foreign)' && (
                            <tr className="sqa-summary-row">
                      <td></td>
                              <td className="sqa-summary-label">Tax</td>
                              <td colSpan={subtotalColspan}></td>
                              <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.tax, quotationData.currency, quotationData.currencySymbol)}</td>
                            </tr>
                          )}
                          
                          <tr className="sqa-summary-row">
                      <td></td>
                            <td className="sqa-summary-label">Shipping Cost</td>
                            <td colSpan={subtotalColspan}></td>
                            <td className="sqa-summary-value">{formatCurrencyWithDecimals(quotationData.pricing.shippingCost, quotationData.currency, quotationData.currencySymbol)}</td>
                    </tr>
                    <tr className="sqa-summary-row sqa-grand-total">
                      <td></td>
                      <td className="sqa-summary-label">Grand Total</td>
                            <td colSpan={subtotalColspan}></td>
                            <td className="sqa-summary-value sqa-grand-total-value">{formatCurrencyWithDecimals(quotationData.pricing.grandTotal, quotationData.currency, quotationData.currencySymbol)}</td>
                    </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Terms & Conditions Section */}
              <div className="sqa-terms-section">
                <div className="sqa-terms-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-terms-icon">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  <h3 className="sqa-terms-title">Terms & Conditions</h3>
                </div>
                
                <div className="sqa-terms-grid">
                  <div className="sqa-terms-item">
                    <div className="sqa-terms-item-header">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" x2="12" y1="2" y2="22"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                      <span className="sqa-terms-item-label">Payment Terms</span>
                    </div>
                    <div className="sqa-terms-item-content sqa-payment-terms-content">
                      <div className="sqa-terms-item-row">
                        <span className="sqa-terms-item-key">Currency:</span>
                        <span className="sqa-terms-item-value">{quotationData.terms.payment.currency}</span>
                      </div>
                      <div className="sqa-terms-item-row">
                        <span className="sqa-terms-item-key">Payment Due Within:</span>
                        <span className="sqa-terms-item-value">{quotationData.terms.payment.paymentDueWithin}</span>
                      </div>
                      <div className="sqa-terms-item-row">
                        <span className="sqa-terms-item-key">Advance Payment:</span>
                        <span className="sqa-terms-item-value">{quotationData.terms.payment.advancePayment}</span>
                      </div>
                      <div className="sqa-terms-item-row">
                        <span className="sqa-terms-item-key">Balance Payment:</span>
                        <span className="sqa-terms-item-value">{quotationData.terms.payment.balancePayment}</span>
                      </div>
                    </div>
                  </div>

                  <div className="sqa-terms-item">
                    <div className="sqa-terms-item-header">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                        <line x1="12" y1="22.08" x2="12" y2="12"></line>
                      </svg>
                      <span className="sqa-terms-item-label">Delivery</span>
                    </div>
                    <div className="sqa-terms-item-content sqa-delivery-terms-content">
                      <div className="sqa-terms-item-row">
                        <span className="sqa-terms-item-key">Lead Time:</span>
                        <span className="sqa-terms-item-value">{quotationData.terms.delivery.leadTime}</span>
                      </div>
                      <div className="sqa-terms-item-row">
                        <span className="sqa-terms-item-key">Incoterms:</span>
                        <span className="sqa-terms-item-value">{quotationData.terms.delivery.incoterms || 'Select Incoterms'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {quotationData.terms.additionalNotes && (
                  <div className="sqa-additional-notes">
                    <div className="sqa-additional-notes-header">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" x2="12" y1="8" y2="12"></line>
                        <line x1="12" x2="12.01" y1="16" y2="16"></line>
                      </svg>
                      <span className="sqa-additional-notes-label">Additional Notes</span>
                    </div>
                    <p className="sqa-additional-notes-text">{quotationData.terms.additionalNotes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Bulk Pricing Tiers Card */}
        <div className="sqa-card">
          <div 
            className="sqa-card-header" 
            onClick={() => toggleSection('bulkPricing')}
          >
            <div className="sqa-card-header-left">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-card-icon lucide lucide-trending-down" aria-hidden="true">
                <path d="M16 17h6v-6"></path>
                <path d="m22 17-8.5-8.5-5 5L2 7"></path>
              </svg>
              <h2 className="sqa-card-title">Bulk Pricing Tiers</h2>
            </div>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              className="sqa-chevron"
              style={{ transform: expandedSections.bulkPricing ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s ease' }}
            >
              <path d="m18 15-6-6-6 6"></path>
            </svg>
          </div>
          {expandedSections.bulkPricing && (
            <div className="sqa-card-content">
              <div className="sqa-table-wrapper">
                <table className="sqa-table">
                  <thead>
                    <tr>
                      <th className="sqa-th-item">#</th>
                      <th className="sqa-th-description">Item Description</th>
                      <th className="sqa-th-right">Tier</th>
                      <th className="sqa-th-right">Quantity Range</th>
                      <th className="sqa-th-right">Unit Price ({quotationData.currencySymbol})</th>
                      <th className="sqa-th-right">Discount (%)</th>
                      <th className="sqa-th-right">Savings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotationData.bulkPricing && quotationData.bulkPricing.length > 0 ? (
                      quotationData.bulkPricing.map((tier, index) => (
                      <tr key={index}>
                          <td className="sqa-td-item">{tier.itemNumber || index + 1}</td>
                          <td className="sqa-td-description">{tier.itemDescription || 'N/A'}</td>
                          <td className="sqa-td-right">Tier {tier.tierNumber || index + 1}</td>
                          <td className="sqa-td-right">{tier.quantityRange}</td>
                          <td className="sqa-td-right">{formatCurrency(tier.unitPrice, quotationData.currency, quotationData.currencySymbol)}</td>
                          <td className="sqa-td-right">
                          {tier.discount > 0 ? (
                            <span className="sqa-discount-green">{tier.discount}% OFF</span>
                          ) : (
                            <span>Base Price</span>
                          )}
                        </td>
                          <td className="sqa-td-right">
                          {tier.savings ? (
                              <span className="sqa-savings-green">Save {formatCurrency(tier.savings, quotationData.currency, quotationData.currencySymbol)} per unit</span>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                      </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                          No bulk pricing available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="sqa-bulk-discount-banner">
                <p className="sqa-bulk-discount-text">
                  💡 <span className="sqa-bulk-discount-label">Bulk Discount Available:</span> Order in larger quantities to save up to 15% on your purchase!
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Attachments & Documents Card */}
        <div className="sqa-card">
          <div 
            className="sqa-card-header" 
            onClick={() => toggleSection('attachments')}
          >
            <div className="sqa-card-header-left">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-card-icon">
                <path d="m21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
              </svg>
              <h2 className="sqa-card-title">Attachments & Documents</h2>
              <span className="sqa-attachment-count">{quotationData.attachments.length}</span>
            </div>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              className="sqa-chevron"
              style={{ transform: expandedSections.attachments ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s ease' }}
            >
              <path d="m18 15-6-6-6 6"></path>
            </svg>
          </div>
          {expandedSections.attachments && (
            <div className="sqa-card-content">
              <div className="sqa-attachments-list">
                {quotationData.attachments.map((attachment, index) => (
                  <div key={index} className="sqa-attachment-item">
                    <div className="sqa-attachment-left">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-attachment-icon">
                        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                        <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                        <path d="M10 9H8"></path>
                        <path d="M16 13H8"></path>
                        <path d="M16 17H8"></path>
                      </svg>
                      <div className="sqa-attachment-info">
                        <div className="sqa-attachment-name">{attachment.name}</div>
                        <div className="sqa-attachment-meta">
                          <span>{attachment.type}</span>
                          <span>•</span>
                          <span>{attachment.size}</span>
                          <span>•</span>
                          <span>Uploaded: {attachment.uploadDate}</span>
                        </div>
                      </div>
                    </div>
                    <div className="sqa-attachment-actions">
                      <button 
                        className="sqa-attachment-link"
                        onClick={() => {
                          const fileUrl = getFileUrl(attachment.url);
                          if (fileUrl) {
                            window.open(fileUrl, '_blank');
                          } else {
                            toast.error('File URL not available');
                          }
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-attachment-link-icon">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                        View
                      </button>
                      <button 
                        className="sqa-attachment-link"
                        onClick={() => {
                          const fileUrl = getFileUrl(attachment.url);
                          if (fileUrl) {
                            // Create a temporary anchor element to trigger download
                            const link = document.createElement('a');
                            link.href = fileUrl;
                            link.download = attachment.name || 'download';
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          } else {
                            toast.error('File URL not available');
                          }
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-attachment-link-icon">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Start Order Modal */}
      {showStartOrderModal && (
        <div className="sqa-modal-overlay" onClick={() => setShowStartOrderModal(false)}>
          <div className="sqa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sqa-modal-header">
              <div className="sqa-modal-header-left">
                <div className="sqa-modal-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <div>
                  <h2 className="sqa-modal-title">Start Order</h2>
                  <p className="sqa-modal-subtitle">Confirm your agreement to the terms and pricing.</p>
                </div>
              </div>
              <button className="sqa-modal-close" onClick={() => setShowStartOrderModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="sqa-modal-body">
              {/* Order Summary */}
              <div className="sqa-order-summary">
                <div className="sqa-summary-item">
                  <span className="sqa-summary-label">RFQ ID:</span>
                  <span className="sqa-summary-value">{quotationData.rfqNumber}</span>
                </div>
                <div className="sqa-summary-item">
                  <span className="sqa-summary-label">Quote ID:</span>
                  <span className="sqa-summary-value">{quotationData.quotationNumber}</span>
                </div>
                <div className="sqa-summary-item">
                  <span className="sqa-summary-label">Supplier Name:</span>
                  <span className="sqa-summary-value">{quotationData.supplier.companyName}</span>
                </div>
                <div className="sqa-summary-item">
                  <span className="sqa-summary-label">Quoted Value:</span>
                  <span className="sqa-summary-value">{formatCurrency(quotationData.grandTotal, quotationData.currency, quotationData.currencySymbol)}</span>
                </div>
              </div>

              {/* Order Information */}
              <div className="sqa-order-info">
                <div className="sqa-form-group">
                  <label className="sqa-form-label">Order Notes / Comments (Optional)</label>
                  <textarea
                    className="sqa-form-textarea"
                    placeholder="Add any special instructions, delivery preferences, or additional comments for this order..."
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    rows="3"
                  />
                  <p className="sqa-form-note">These notes will be shared with the supplier</p>
                </div>

                <div className="sqa-form-group">
                  <label className="sqa-form-label">Attachment (Optional)</label>
                  <div className="sqa-file-upload">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-file-icon">
                      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                      <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                      <path d="M10 9H8"></path>
                      <path d="M16 13H8"></path>
                      <path d="M16 17H8"></path>
                    </svg>
                    <input
                      type="file"
                      ref={orderFileInputRef}
                      onChange={handleOrderFileChange}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    />
                    <button type="button" className="sqa-file-btn" onClick={handleOrderFileClick}>Choose File</button>
                    <span className="sqa-file-text">
                      {orderFile ? orderFile.name : 'Upload purchase order, specifications, or other docume...'}
                    </span>
                  </div>
                </div>

                <div className="sqa-form-row">
                  <div className="sqa-form-group sqa-form-group-half">
                    <label className="sqa-form-label">Accepted By</label>
                    <input type="text" className="sqa-form-input" value={buyerName} readOnly />
                  </div>
                  <div className="sqa-form-group sqa-form-group-half">
                    <label className="sqa-form-label">Accepted On</label>
                    <input type="text" className="sqa-form-input" defaultValue={formatDateTime()} readOnly />
                  </div>
                </div>
              </div>

              {/* Notification Settings */}
              <div className="sqa-notification-settings">
                <label className="sqa-checkbox-label">
                  <input
                    type="checkbox"
                    className="sqa-checkbox"
                    checked={notifySupplier}
                    onChange={(e) => setNotifySupplier(e.target.checked)}
                  />
                  <span className="sqa-checkbox-custom"></span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-envelope-icon">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <span className="sqa-checkbox-text">Notify Supplier via Email</span>
                </label>
                <p className="sqa-notification-note">An automated email will be sent to the supplier confirming the order</p>
              </div>
            </div>

            <div className="sqa-modal-footer">
              <button className="sqa-modal-btn sqa-modal-btn-cancel" onClick={() => setShowStartOrderModal(false)}>
                Cancel
              </button>
              <button 
                type="button"
                className="sqa-modal-btn sqa-modal-btn-confirm" 
                onClick={handleConfirmOrder}
                disabled={processing}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
                {processing ? 'Processing...' : 'Confirm Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quality Inspector Evaluation Modal */}
      {showQualityInspectorModal && (
        <div className="sqa-modal-overlay" onClick={() => setShowQualityInspectorModal(false)}>
          <div className="sqa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sqa-modal-header">
              <div className="sqa-modal-header-left">
                <div className="sqa-modal-icon" style={{ backgroundColor: '#DBEAFE' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12l2 2 4-4"></path>
                    <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"></path>
                    <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"></path>
                    <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3"></path>
                    <path d="M12 21c0-1 1-3 3-3s3 2 3 3-1 3-3 3-3-2-3-3"></path>
                  </svg>
                </div>
                <div>
                  <h2 className="sqa-modal-title">Quality Inspector Evaluation</h2>
                  <p className="sqa-modal-subtitle">Do you want to evaluate using a Quality Inspector?</p>
                </div>
              </div>
              <button className="sqa-modal-close" onClick={() => setShowQualityInspectorModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="sqa-modal-body">
              <div style={{ padding: '20px 0', textAlign: 'center' }}>
                <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>
                  Would you like to request a Quality Inspector to evaluate this accepted quotation?
                </p>
              </div>
            </div>

            <div className="sqa-modal-footer">
              <button 
                className="sqa-modal-btn sqa-modal-btn-cancel" 
                onClick={() => {
                  setShowQualityInspectorModal(false);
                  // Navigate to purchase order list
                  navigate('/purchase-order-list', { replace: false });
                }}
              >
                No
              </button>
              <button 
                className="sqa-modal-btn sqa-modal-btn-confirm" 
                onClick={() => {
                  setShowQualityInspectorModal(false);
                  // Navigate to Buyer Request QA page with quotation_id and rfq_id
                  const qId = quotationId;
                  const rId = rfqData?.rfq_id;
                  if (qId && rId) {
                    navigate(`/buyer-qa-request?quotation_id=${qId}&rfq_id=${rId}`, { replace: false });
                  } else {
                    toast.error('Missing quotation or RFQ information');
                    navigate('/purchase-order-list', { replace: false });
                  }
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Quote Modal */}
      {showRejectModal && (
        <div className="sqa-modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="sqa-modal sqa-reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sqa-modal-header">
              <div className="sqa-modal-header-left">
                <div className="sqa-modal-icon sqa-modal-icon-reject">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="m15 9-6 6"></path>
                    <path d="m9 9 6 6"></path>
                  </svg>
                </div>
                <div>
                  <h2 className="sqa-modal-title">Reject Quote</h2>
                  <p className="sqa-modal-subtitle">Provide reason for rejecting this quotation.</p>
                </div>
              </div>
              <button className="sqa-modal-close" onClick={() => setShowRejectModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="sqa-modal-body">
              {/* Quote Details */}
              <div className="sqa-order-summary">
                <div className="sqa-summary-item">
                  <span className="sqa-summary-label">RFQ ID:</span>
                  <span className="sqa-summary-value">{quotationData.rfqNumber}</span>
                </div>
                <div className="sqa-summary-item">
                  <span className="sqa-summary-label">Quote ID:</span>
                  <span className="sqa-summary-value">{quotationData.quotationNumber}</span>
                </div>
                <div className="sqa-summary-item">
                  <span className="sqa-summary-label">Supplier Name:</span>
                  <span className="sqa-summary-value">{quotationData.supplier.companyName}</span>
                </div>
                <div className="sqa-summary-item">
                  <span className="sqa-summary-label">Quoted Value:</span>
                  <span className="sqa-summary-value">{formatCurrency(quotationData.grandTotal, quotationData.currency, quotationData.currencySymbol)}</span>
                </div>
              </div>

              {/* Rejection Information */}
              <div className="sqa-order-info">
                <div className="sqa-form-group">
                  <label className="sqa-form-label">Reject Reason <span className="sqa-required">*</span></label>
                  <select
                    className="sqa-form-select"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  >
                    <option value="">Select a reason</option>
                    <option value="price-too-high">Price too high</option>
                    <option value="quality-issues">Quality issues</option>
                    <option value="delivery-time">Delivery time not acceptable</option>
                    <option value="terms-not-suitable">Terms not suitable</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="sqa-form-group">
                  <label className="sqa-form-label">Detailed Remarks <span className="sqa-required">*</span></label>
                  <textarea
                    className="sqa-form-textarea"
                    placeholder="Enter detailed explanation for rejection..."
                    value={detailedRemarks}
                    onChange={(e) => setDetailedRemarks(e.target.value)}
                    rows="3"
                  />
                  <p className="sqa-form-note">Provide specific reasons to help the supplier understand your decision</p>
                </div>

                <div className="sqa-form-group">
                  <label className="sqa-form-label">Supporting Document</label>
                  <div className="sqa-file-upload">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-file-icon">
                      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                      <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                      <path d="M10 9H8"></path>
                      <path d="M16 13H8"></path>
                      <path d="M16 17H8"></path>
                    </svg>
                    <input
                      type="file"
                      ref={rejectFileInputRef}
                      onChange={handleRejectFileChange}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    />
                    <button type="button" className="sqa-file-btn" onClick={handleRejectFileClick}>Choose File</button>
                    <span className="sqa-file-text">
                      {rejectFile ? rejectFile.name : 'Upload any supporting documents (optional)'}
                    </span>
                </div>
              </div>

              {/* Notification Settings */}
              <div className="sqa-notification-settings sqa-notification-settings-reject">
                <label className="sqa-checkbox-label">
                  <input
                    type="checkbox"
                    className="sqa-checkbox"
                    checked={notifySupplierReject}
                    onChange={(e) => setNotifySupplierReject(e.target.checked)}
                  />
                  <span className="sqa-checkbox-custom"></span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-envelope-icon">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <span className="sqa-checkbox-text">Notify Supplier via Email</span>
                </label>
                <p className="sqa-notification-note">An automated email will be sent to the supplier with the rejection details</p>
                </div>

                <div className="sqa-form-row">
                  <div className="sqa-form-group sqa-form-group-half">
                    <label className="sqa-form-label">Rejected By</label>
                    <input type="text" className="sqa-form-input" value={buyerName} readOnly />
                  </div>
                  <div className="sqa-form-group sqa-form-group-half">
                    <label className="sqa-form-label">Rejected On</label>
                    <input type="text" className="sqa-form-input" defaultValue={formatDateTime()} readOnly />
                  </div>
                </div>
              </div>
            </div>

            <div className="sqa-modal-footer">
              <button className="sqa-modal-btn sqa-modal-btn-cancel" onClick={() => setShowRejectModal(false)}>
                Cancel
              </button>
              <button 
                className="sqa-modal-btn sqa-modal-btn-reject"
                disabled={!rejectReason || !detailedRemarks || processing}
                onClick={handleRejectQuotation}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sqa-reject-icon">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="m15 9-6 6"></path>
                  <path d="m9 9 6 6"></path>
                </svg>
                {processing ? 'Processing...' : 'Reject Quotation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
